/**
 * Motherboard Data Adapter
 *
 * Provides unified data loading for Motherboard components using hybrid approach:
 * 1. List files (11 essential fields for list views)
 * 2. Detail files (Raw v2.0.0 schema with full specifications)
 * 3. Pricing files (regularPrice, currentPrice)
 *
 * This adapter provides both:
 * - BaseDataLoader-compatible interface (getAllItems, getCurrentItems, getDiscontinuedItems)
 * - Raw detail loading with conversion (getItemDetail, getRawDetail)
 *
 * Usage:
 * ```typescript
 * const adapter = getMotherboardDataAdapter()
 * const allMBs = await adapter.getAllItems()           // List view
 * const mbDetail = await adapter.getItemDetail('id')   // Detail view
 * ```
 */

import { createSingletonAdapter, type PriceInfo, type PricingData } from '../common/GenericDataAdapter'
import type { MotherboardListItem } from '../../models/motherboard/motherboard-list-model'
import { isMotherboardRawModel, type MotherboardRawModel } from '../../models/motherboard/motherboard-raw-model'
import type { MotherboardDetailModel } from '../../models/motherboard/motherboard-detail-model'
import type { CPUSocketConfig, FormFactor, ComponentStatus } from '../../models/motherboard/motherboard-model'

/**
 * Motherboard Data Adapter Class
 *
 * Combines List/Pricing loading with Raw detail conversion
 */
class MotherboardDataAdapter {
  private readonly basePath = '/data/motherboard'
  private readonly genericAdapter = createSingletonAdapter<MotherboardListItem>({
    basePath: this.basePath,
    componentName: 'motherboard',
    pricingTTL: 300000, // 5 minutes
    listTTL: 600000 // 10 minutes
  }).getAdapter()

  private listCache: Map<string, MotherboardListItem[]> = new Map()
  private pricingCache: Map<string, PriceInfo> = new Map()
  private detailCache: Map<string, MotherboardRawModel> = new Map()
  private pricingLoadTime = 0
  private readonly PRICING_TTL = 300000 // 5 minutes

  /**
   * Load list file (current or discontinued)
   */
  private async loadListFile(status: 'current' | 'discontinued'): Promise<MotherboardListItem[]> {
    const cacheKey = `motherboard-${status}-list`

    if (this.listCache.has(cacheKey)) {
      return this.listCache.get(cacheKey)!
    }

    const response = await fetch(`${this.basePath}/motherboard-${status}-list.json`)
    if (!response.ok) {
      throw new Error(`Failed to load Motherboard ${status} list: ${response.statusText}`)
    }

    const data: MotherboardListItem[] = await response.json()
    this.listCache.set(cacheKey, data)
    return data
  }

  /**
   * Load pricing file (current or discontinued)
   */
  private async loadPricingFile(status: 'current' | 'discontinued'): Promise<Map<string, PriceInfo>> {
    const now = Date.now()

    // Check if pricing data is stale
    if (this.pricingCache.size > 0 && (now - this.pricingLoadTime) < this.PRICING_TTL) {
      return this.pricingCache
    }

    const response = await fetch(`${this.basePath}/motherboard-${status}-prices.json`)
    if (!response.ok) {
      console.warn(`Failed to load Motherboard ${status} pricing: ${response.statusText}`)
      return new Map()
    }

    const data: PricingData = await response.json()
    const priceMap = new Map<string, PriceInfo>()

    if (data?.prices) {
      Object.entries(data.prices).forEach(([id, price]) => {
        priceMap.set(id, price)
      })
    }

    this.pricingLoadTime = now
    return priceMap
  }

  /**
   * Merge list item with pricing data
   */
  private mergeWithPricing(item: MotherboardListItem, priceInfo?: PriceInfo): MotherboardModel {
    return {
      ...item,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null
    } as MotherboardModel
  }

  /**
   * Get all Motherboard items (Current + Discontinued)
   * Compatible with: loader.getAllItems()
   */
  async getAllItems(): Promise<MotherboardModel[]> {
    const [currentList, discontinuedList, currentPricing, discontinuedPricing] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued').catch(() => [] as MotherboardListItem[]),
      this.loadPricingFile('current'),
      this.loadPricingFile('discontinued').catch(() => new Map<string, PriceInfo>())
    ])

    const currentWithPricing = currentList.map(item =>
      this.mergeWithPricing(item, currentPricing.get(item.id))
    )
    const discontinuedWithPricing = discontinuedList.map(item =>
      this.mergeWithPricing(item, discontinuedPricing.get(item.id))
    )

    return [...currentWithPricing, ...discontinuedWithPricing]
  }

  /**
   * Get current Motherboard items only
   * Compatible with: loader.getCurrentItems()
   */
  async getCurrentItems(): Promise<MotherboardModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('current'),
      this.loadPricingFile('current')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get discontinued Motherboard items only
   * Compatible with: loader.getDiscontinuedItems()
   */
  async getDiscontinuedItems(): Promise<MotherboardModel[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('discontinued').catch(() => [] as MotherboardListItem[]),
      this.loadPricingFile('discontinued').catch(() => new Map<string, PriceInfo>())
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get detail data for a specific Motherboard (with full specifications)
   * Loads Raw v2.0.0 detail file and converts to MotherboardDetailModel
   */
  async getItemDetail(itemId: string): Promise<MotherboardDetailModel> {
    // Load raw detail file
    let rawDetail = this.detailCache.get(itemId)

    if (!rawDetail) {
      const response = await fetch(`${this.basePath}/details/${itemId}.json`)
      if (!response.ok) {
        throw new Error(`Failed to load Motherboard detail: ${response.statusText}`)
      }
      const rawData = await response.json()

      // Validate raw schema
      if (!isMotherboardRawModel(rawData)) {
        throw new Error(`Invalid Motherboard data format for: ${itemId}`)
      }

      rawDetail = rawData
      this.detailCache.set(itemId, rawDetail)
    }

    // Load pricing data
    await this.loadPricingFile('current')
    const priceInfo = this.pricingCache.get(itemId)

    // Convert raw to detail model and merge with pricing
    const detailModel = this.rawToDetailModel(rawDetail)
    return {
      ...detailModel,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null,
      ...(priceInfo?.asin !== undefined && { asin: priceInfo.asin })
    }
  }

  /**
   * Get raw detail data without conversion
   */
  async getRawDetail(itemId: string): Promise<MotherboardRawModel> {
    let rawDetail = this.detailCache.get(itemId)

    if (!rawDetail) {
      const response = await fetch(`${this.basePath}/details/${itemId}.json`)
      if (!response.ok) {
        throw new Error(`Failed to load Motherboard detail: ${response.statusText}`)
      }
      const rawData = await response.json()

      if (!isMotherboardRawModel(rawData)) {
        throw new Error(`Invalid Motherboard data format for: ${itemId}`)
      }

      rawDetail = rawData
      this.detailCache.set(itemId, rawDetail)
    }

    return rawDetail
  }

  /**
   * Convert raw detail to list item format
   * Used for compatibility with existing list views
   */
  rawToListItem(raw: MotherboardRawModel): MotherboardListItem {
    return {
      id: raw.id,
      model: raw.productName,
      manufacturer: raw.manufacturer.name,
      status: 'Current' as ComponentStatus,

      // Core specs
      chipset: `${raw.chipset.manufacturer} ${raw.chipset.name}`,
      cpuSocket: this.convertSocket(raw.socket),
      formFactor: raw.formFactor as FormFactor,

      // Memory
      memoryType: `DIMM ${raw.memory.type}`,
      memorySlots: raw.memory.slots,
      maxMemoryCapacity: `${raw.memory.maxCapacityGb}GB`,

      // Storage
      m2Slots: raw.storage?.m2Slots?.length ?? 0
    }
  }

  /**
   * Convert raw detail to full detail model format
   * Passes v2 Raw Schema data through directly (no legacy conversion)
   */
  rawToDetailModel(raw: MotherboardRawModel): MotherboardDetailModel {
    const listItem = this.rawToListItem(raw)

    return {
      ...listItem,

      // v2 Raw Schema fields passed through directly
      ...(raw.series !== undefined && { series: raw.series }),
      ...(raw.officialUrl !== undefined && { officialUrl: raw.officialUrl }),
      manufacturerInfo: raw.manufacturer,
      chipsetInfo: raw.chipset,
      memorySpec: raw.memory,
      ...(raw.pcieSlots !== undefined && { pcieSlots: raw.pcieSlots }),
      ...(raw.totalExpansionSlots !== undefined && { totalExpansionSlots: raw.totalExpansionSlots }),
      ...(raw.storage !== undefined && { storage: raw.storage }),
      ...(raw.network !== undefined && { network: raw.network }),
      ...(raw.audio !== undefined && { audio: raw.audio }),
      ...(raw.usb !== undefined && { usb: raw.usb }),
      ...(raw.thunderbolt !== undefined && { thunderbolt: raw.thunderbolt }),
      ...(raw.internalHeaders !== undefined && { internalHeaders: raw.internalHeaders }),
      ...(raw.rearIO !== undefined && { rearIO: raw.rearIO }),
      ...(raw.bios !== undefined && { bios: raw.bios }),
      ...(raw.vrm !== undefined && { vrm: raw.vrm }),
      ...(raw.power !== undefined && { power: raw.power }),
      ...(raw.dimensions !== undefined && { dimensions: raw.dimensions })
    }
  }

  /**
   * Convert socket string to CPUSocketConfig
   */
  private convertSocket(socket: string): CPUSocketConfig {
    // Handle common socket formats
    const socketMap: Record<string, { type: string; label: string }> = {
      AM5: { type: 'AM5', label: 'Socket AM5' },
      AM4: { type: 'AM4', label: 'Socket AM4' },
      LGA1700: { type: 'LGA1700', label: 'LGA 1700' },
      LGA1200: { type: 'LGA1200', label: 'LGA 1200' },
      LGA1151: { type: 'LGA1151', label: 'LGA 1151' },
      TR5: { type: 'TR5', label: 'Socket sTR5' },
      sTRX4: { type: 'sTRX4', label: 'Socket sTRX4' },
      sWRX8: { type: 'sWRX8', label: 'Socket sWRX8' }
    }

    return socketMap[socket] ?? { type: socket, label: socket }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.listCache.clear()
    this.pricingCache.clear()
    this.detailCache.clear()
    this.pricingLoadTime = 0
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      listCacheSize: this.listCache.size,
      pricingCacheSize: this.pricingCache.size,
      detailCacheSize: this.detailCache.size,
      pricingCacheAge: Date.now() - this.pricingLoadTime
    }
  }
}

// Singleton instance
let instance: MotherboardDataAdapter | null = null

/**
 * Get the singleton Motherboard data adapter instance
 */
export function getMotherboardDataAdapter(): MotherboardDataAdapter {
  instance ??= new MotherboardDataAdapter()
  return instance
}

/**
 * Reset the Motherboard data adapter singleton (useful for testing)
 */
export function resetMotherboardDataAdapter(): void {
  instance = null
}

/**
 * Convert raw motherboard data to list item format
 * Standalone utility function for use in conversion scripts
 */
export function rawToListItem(raw: MotherboardRawModel): MotherboardListItem {
  const adapter = getMotherboardDataAdapter()
  return adapter.rawToListItem(raw)
}
